import React, { useState, useEffect } from "react";
import LayoutDumpster from "../components/layout-dumpster";
import LayoutDemolition from "../components/layout-demolition";
import { BusinessSearch } from '@hometown/react-hometown-lib';
import { ApolloClient, InMemoryCache, HttpLink } from '@apollo/client';
import { GET_PROS_NEARME } from '../components/nearmeQueryDefinition';

const defaultOptions = {
    watchQuery: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore',
    },
    query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
    },
};

/**
 * Pro Registration Page Template
 *
 * This is the entry point for Pro Registration flow (Phase 1).
 * Users search for their business name or add a new one.
 *
 * Path 1: User selects "Add business with this name" -> redirects to Bookable /pro/register
 * Path 2: User selects an existing business -> redirects to Bookable /pro/register/claim/:proId
 */
const ProRegistration = ({ pageContext }) => {
    const code = pageContext.code;
    const env = pageContext.env;
    const bookableUrl = pageContext.bookableUrl;
    const apolloUrl = pageContext.apolloUrl;

    const [placeName, setPlaceName] = useState('');

    const seo = {
        url: 'pro/register',
        env: env,
        code: code,
        page: 'pro/register',
        title: 'Pro Registration | Hometown',
        description: 'Register your business on Hometown. Search for your existing business or add a new one to get started.'
    };

    // Apollo client for location query
    const headers = {};
    headers['Ocp-Apim-Subscription-Key'] = process.env.GATSBY_API_MANAGMENT_KEY;

    const client = new ApolloClient({
        link: new HttpLink({ uri: `${process.env.GATSBY_BOOKABLE_SEARCH_API}`, headers: headers }),
        cache: new InMemoryCache(),
        defaultOptions: defaultOptions
    });

    // Site-aware layout: demolition uses its own layout/header
    const Layout = env.site === 'demolition' ? LayoutDemolition : LayoutDumpster;

    // Non-prod fallback zip (Grand Rapids, MI) for when geolocation/IP can't
    // resolve a valid US place. We can't rely on $DOT_ENV here because dev also
    // builds with $DOT_ENV=production. Instead we detect the environment by the
    // main site domain: dev/staging are served from the Azure static blob
    // (e.g. hometownstaticsites.z19.web.core.windows.net /
    // hometownstaticdemolition.z19.web.core.windows.net), while production uses
    // the real domain. So the fallback is active off the blob host, never in prod.
    const DEV_FALLBACK_ZIP = '49506';
    const siteDomain = env.domain || '';
    const isDev = siteDomain.includes('z19') || siteDomain.includes('web.core.windows.net');

    // Fetch location info (place name) based on zip code
    const fetchLocation = (zip) => {
        client.query({
            query: GET_PROS_NEARME,
            variables: {
                page: 1,
                limit: 1,
                zipcode: zip || (isDev ? DEV_FALLBACK_ZIP : null),
                // zipcode: '49506',
                projectType: '1',
                site: env.site
            }
        }).then(result => {
            if (result.data?.nearmeDefinition?.place) {
                setPlaceName(result.data.nearmeDefinition.place);
            } else if (isDev && zip !== DEV_FALLBACK_ZIP) {
                // Dev-only: no valid place resolved, retry with Grand Rapids
                fetchLocation(DEV_FALLBACK_ZIP);
            }
        }).catch(e => {
            console.error('Error fetching location:', e);
        });
    };

    // Fetch location on mount (uses geolocation/IP if no zip provided)
    useEffect(() => {
        fetchLocation(null);
    }, []);

    // Handle location change from BusinessSearch component
    const handleLocationChange = ({ zipCode: newZip }) => {
        fetchLocation(newZip);
    };

    // Path 1: Add new business - pass data via URL params (sessionStorage doesn't work cross-domain)
    const handleAddNewBusiness = (businessName) => {
        if (typeof window !== 'undefined') {
            const params = new URLSearchParams({
                businessName: businessName,
                isNew: 'true'
            });
            window.location.href = `${bookableUrl}/pro/register?${params.toString()}`;
        }
    };

    // Path 2: Claim existing business - pass data via URL params
    const handleSelectBusiness = (business) => {
        if (typeof window !== 'undefined') {
            const params = new URLSearchParams({
                businessName: business.name || '',
                claimedBusinessEmail: business.email || '',
                claimedBusinessPhone: business.business_phone || '',
                proId: business.id || ''
            });
            window.location.href = `${bookableUrl}/pro/register/claim/${business.id}`;
        }
    };

    return (
        <Layout seo={seo} env={env} code={code} isProsPortal={true}>
            <BusinessSearch
                apiUrl={apolloUrl}
                onAddNewBusiness={handleAddNewBusiness}
                onSelectBusiness={handleSelectBusiness}
                placeName={placeName}
                onLocationChange={handleLocationChange}
            />
        </Layout>
    );
};

export default ProRegistration;
